import { SHELL_BUNDLE_NAMES } from "@slot/game-shell";
import type { GameAudioOverrides } from "@slot/game-shell";

/**
 * Bloody Teddy's sounds over the shell's default bank: the reels' land and roll sounds, the VS battle's stinger, the
 * scatter's, the win cue, the feature music (VS Rush on its own loop), and the mix (the Cocos title's values). The
 * Cocos bank's cascade-era ids (`character_multiplier_drop`, `grid_multiplier_*`, `grid_remove_symbols`) and the
 * unshipped `grid_win_highlight` are not carried over: nothing in this title plays them.
 */
export function createGameAudioOverrides(): GameAudioOverrides {
  const AUDIO_BUNDLE = { kind: "local" as const, name: SHELL_BUNDLE_NAMES.audio };
  const sfx = (id: string, opts: Record<string, unknown> = {}) => ({
    id,
    locator: { kind: "bundle_audio" as const, bundle: AUDIO_BUNDLE, key: `audio/${id}` },
    category: "sfx" as const,
    defaultOpts: { priority: 5, stopOnSlam: false, protectFromSteal: false, ...opts },
  });
  const music = (id: string, gain?: number) => ({
    id,
    locator: { kind: "bundle_audio" as const, bundle: AUDIO_BUNDLE, key: `audio/${id}` },
    category: "music" as const,
    ...(gain !== undefined ? { defaultOpts: { gain } } : {}),
  });

  return {
    mapping: {
      "ui.click": "ui_click",
      // The land thud (the reels play it once per landed column, `DropThud`) and the scatter's.
      "grid.column_drop": "column_drop",
      "grid.scatter_drop": "grid_scatter_drop",
      "grid.scatter_collect": "grid_scatter_collect",
      // The win cue climbs three pitches across a spin's wins (the win sequence's cue ladder, createPresentation).
      "grid.win_highlight": "grid_win_highlight_pitch_1",
      "grid.win_highlight.2": "grid_win_highlight_pitch_2",
      "grid.win_highlight.3": "grid_win_highlight_pitch_3",
      // The base ⇄ feature transition: no visual, this sound.
      "game.transition": "game_transition",
    },

    featureStateMusicMap: {
      super_bonus_buy: "music_feature_free_spins",
      vs_rush_buy: "music_feature_free_spins_2",
    },

    ambientTrackId: "music_ambient",

    // Game-only sounds that must be ready on their first call (a one-shot not yet decoded is lost; a music track not
    // yet decoded does not start).
    preloadIds: [
      "grid_win_highlight_pitch_1",
      "grid_win_highlight_pitch_2",
      "grid_win_highlight_pitch_3",
      "column_drop",
      "grid_scatter_drop",
      "column_vs_drop",
      "grid_scatter_collect",
      "reel_roll",
      "reel_anticipation_roll",
      "vs_battle",
      "game_transition",
      "music_feature_free_spins",
      "music_feature_free_spins_2",
      "music_feature_idle",
      "music_feature_finished_intro",
    ],

    // Per-id tweaks on shell-registered sounds: a louder spin press; the finished panel's idle bed silenced (it
    // plays between the finished intro and the base loop).
    defaultOptsOverrides: {
      spin_start: { gain: 1.3 },
      music_feature_idle: { gain: 0 },
    },

    banks: [
      {
        id: "game-audio",
        assets: [
          ...[1, 2, 3].map((pitch) => sfx(`grid_win_highlight_pitch_${pitch}`, { gain: 1.2 })),
          {
            id: "music_ambient",
            locator: { kind: "bundle_audio", bundle: AUDIO_BUNDLE, key: "audio/music_ambient" },
            category: "ambient",
            defaultOpts: { gain: 1 },
          },
          // The battle's stinger: played by id, never stolen by a lesser sound.
          sfx("vs_battle", { priority: 20, protectFromSteal: true, gain: 1 }),
          sfx("game_transition", { priority: 30, protectFromSteal: true }),
          // The land thud, once per landed column unless a symbol's own land sound replaces it.
          sfx("column_drop", { randomPlaybackRateMin: 0.9, randomPlaybackRateMax: 1.1 }),
          // The drum's loops: the roll for the whole spin, the anticipation roll over the tease (the roll ducked under it).
          sfx("reel_roll", { gain: 1.2 }),
          sfx("reel_anticipation_roll", { gain: 3 }),
          sfx("grid_scatter_drop", { gain: 1 }),
          sfx("column_vs_drop", { gain: 2 }),
          sfx("grid_scatter_collect", { gain: 3 }),
          music("music_feature_free_spins", 2),
          music("music_feature_free_spins_2", 2),
          music("music_feature_idle"),
          music("music_feature_finished_intro", 2),
        ],
      },
    ],

    buses: { music: true, sfx: true, ui: true, voice: true, ambient: true },
    busVolumes: { music: 0.4, sfx: 0.7, ui: 0.7, voice: 1.0, ambient: 0.6 },
  };
}
